/**
 * 家长点评接口（见方案 3.4.4、4.5b）。
 *
 * | 方法 | 路径 |
 * | --- | --- |
 * | POST | `/comments` |
 * | GET | `/comments?targetType&targetId` |
 * | GET | `/students/:id/comments/weekly` |
 *
 * 点评是"积分的配套"而非附加功能：只有分没有话，孩子会把它理解成冷冰冰的交易。
 * 因此这里提供了快捷短语与情绪标记（低龄模式用图形化表情替代长文本）。
 */
import { api, tryRequest } from './client';
import { pickArray, pickObject, toNumber } from './shape';
import type { Comment } from './types';

/** 低龄模式的图形化情绪标记（见方案 3.4.4）。 */
export const COMMENT_MOODS = ['great', 'good', 'keep_going'] as const;
export type CommentMood = (typeof COMMENT_MOODS)[number];

export const MOOD_LABEL: Record<CommentMood, string> = {
  great: '😄 很棒',
  good: '🙂 不错',
  keep_going: '😐 加油',
};

/** 快捷短语：家长懒得写，这个功能就会死掉，所以必须降低操作成本。 */
export const QUICK_PHRASES = [
  '很棒！',
  '今天专注度很高',
  '明天再快一点',
  '坚持住，我看到了',
  '这次比上次进步了',
] as const;

function normalizeComment(raw: unknown): Comment | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id : '';
  if (!id) return null;
  const targetType =
    record.targetType === 'node' ||
    record.targetType === 'redemption' ||
    record.targetType === 'period'
      ? record.targetType
      : 'checkin';
  const mood =
    record.mood === 'great' || record.mood === 'good' || record.mood === 'keep_going'
      ? record.mood
      : null;

  return {
    id,
    studentId: typeof record.studentId === 'string' ? record.studentId : undefined,
    targetType,
    targetId: typeof record.targetId === 'string' ? record.targetId : '',
    authorType:
      record.authorType === 'student' || record.authorType === 'system' ? record.authorType : 'parent',
    authorId: typeof record.authorId === 'string' ? record.authorId : null,
    authorNickname: typeof record.authorNickname === 'string' ? record.authorNickname : null,
    content: typeof record.content === 'string' ? record.content : '',
    mood,
    createdAt: typeof record.createdAt === 'string' ? record.createdAt : '',
  };
}

export interface CommentListResult {
  comments: Comment[];
  notReady: boolean;
}

/**
 * 点评列表。
 *
 * ⚠️ `studentId` **不是可选的装饰**：后端对 `targetType='period'`（"这段时间的成长"）
 *   无法从 targetId 反查学生（见 `comments.service.targetStudentId` 的 default 分支
 *   返回 null），因此**必须显式带上 `studentId`**，否则接口返回
 *   `400 VALIDATION_FAILED { field: 'studentId' }`。
 *
 *   这个 bug 的表现是：孩子详情页/积分页的点评区**一直在后台报 400**、列表永远为空，
 *   而界面上只是"没有点评"—— 看起来像"还没人写"，不像报错。
 */
export async function listComments(
  targetType: Comment['targetType'],
  targetId: string,
  studentId?: string,
): Promise<CommentListResult> {
  const result = await tryRequest(() =>
    api.get<unknown>('/comments', {
      targetType,
      targetId,
      ...(studentId ? { studentId } : {}),
    }),
  );
  if (result.state === 'notReady') return { comments: [], notReady: true };
  return {
    // ⚠️ 后端分页列表**统一用 `entries`**（本仓的 checkins / redemptions / comments /
    //   points-ledger 都是这个键）。早先候选只有 `comments` / `items` → 恒为空，
    //   表现是"点评框发了却看不到任何点评"。
    comments: pickArray<unknown>(result.data, 'entries', 'comments', 'items')
      .map(normalizeComment)
      .filter((comment): comment is Comment => comment !== null),
    notReady: false,
  };
}

export async function createComment(body: {
  targetType: Comment['targetType'];
  targetId: string;
  content: string;
  mood: CommentMood | null;
  /** 同 `listComments`：`period` 类型必传，否则后端 400。 */
  studentId?: string;
}): Promise<void> {
  await api.post('/comments', body);
}

/** 本周小结草稿（家长改两句就能发）。 */
export async function getWeeklyDraft(studentId: string): Promise<string | null> {
  const result = await tryRequest(() =>
    api.get<unknown>(`/students/${studentId}/comments/weekly`),
  );
  if (result.state === 'notReady') return null;
  const record = pickObject(result.data, {}) as Record<string, unknown>;
  if (typeof record.draft === 'string') return record.draft;
  if (typeof record.content === 'string') return record.content;
  if (typeof record.text === 'string') return record.text;
  return null;
}

/** 供 UI 显示"共几条点评"（不额外请求，仅用来做防御性读取）。 */
export function commentCountLabel(count: unknown): string {
  return `${toNumber(count, 0)} 条点评`;
}
